<script setup>
import { ref, onMounted, onUnmounted, watch } from "vue";
import i18n from "@/i18n";

import { useVideoCallState } from "@/stores/videoCallState";
import { EventManager, Events } from "@events";
import axios from "axios";
import { getCannedResponses } from "../composables/cannedResponsesCache";

const props = defineProps({
  rtcId: {
    type: String,
    required: true,
  },
  sectionType: {
    type: String,
    required: false,
  },
  modelValue: {
    type: Object,
    required: false,
  },
});

const snippetActive = ref(false);
const activeSnippetId = ref(null);
const recordingStartTime = ref(null);

const state = useVideoCallState(props.rtcId);
let eventEmitter = null;
let actionEmitter = null;

const description = ref(null);
const cannedResponses = ref([]);
const isStopping = ref(false);
const showDescription = ref(false);
const elapsedLabel = ref("0:00");
let snippetStartTime = null;
let timerInterval = null;
const cleanupFns = [];

function formatElapsed(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function startTimer(offsetSeconds = 0) {
  snippetStartTime = Date.now() - offsetSeconds * 1000;
  elapsedLabel.value = formatElapsed(offsetSeconds);
  timerInterval = setInterval(() => {
    const secs = Math.floor((Date.now() - snippetStartTime) / 1000);
    elapsedLabel.value = formatElapsed(secs);
  }, 1000);
}

function stopTimer() {
  if (timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
  }
}

onUnmounted(() => {
  stopTimer();
  cleanupFns.forEach((fn) => fn());
});

// Auto-close the description panel when a value is selected from the dropdown
watch(description, (val) => {
  if (val && cannedResponses.value.includes(val)) {
    showDescription.value = false;
  }
});

onMounted(async () => {
  eventEmitter = EventManager.getEventEmitter(props.rtcId);
  actionEmitter = EventManager.getActionEmitter(props.rtcId);

  // Listen for snippet start from SnippetBtn
  const onSnippetStarted = (data) => {
    snippetActive.value = true;
    activeSnippetId.value = data.snippetId;
    recordingStartTime.value = data.recordingStartTime;
    showDescription.value = false;
    startTimer(data.offsetSeconds || 0);
  };

  actionEmitter.on("snippet-started", onSnippetStarted);
  cleanupFns.push(() => actionEmitter.off("snippet-started", onSnippetStarted));

  try {
    cannedResponses.value = await getCannedResponses();
  } catch (error) {
    console.error("Failed to load canned responses", error);
  }
});

function toggleDescription() {
  showDescription.value = !showDescription.value;
}

async function stopSnippet() {
  if (isStopping.value) return;
  isStopping.value = true;

  const elapsedSeconds = recordingStartTime.value
    ? Math.floor((Date.now() - recordingStartTime.value) / 1000)
    : 0;

  try {
    const response = await axios.post(
      `/api/Snippet/StopSnippet/${props.rtcId}/${activeSnippetId.value}`,
      {
        endSecond: elapsedSeconds,
        description: description.value,
      }
    );

    if (response.status === 200 && response.data?.success) {
      eventEmitter.emit(Events.CallEvent, {
        name: "SnippetStopped",
        // Store only the language-neutral second marker; admin renders the event text in the viewer's language.
        data: `${elapsedSeconds}s`,
      });

      showSnackbar(i18n.global.t("SnippetSuccess"));
    } else {
      showSnackbar(i18n.global.t("SnippetFailed"), "red");
    }
  } catch (error) {
    console.error(error);
    showSnackbar(i18n.global.t("SnippetFailed"), "red");
  } finally {
    isStopping.value = false;
    description.value = null;
    showDescription.value = false;
    stopTimer();
    snippetActive.value = false;
    activeSnippetId.value = null;
    if (actionEmitter) {
      actionEmitter.emit("snippet-stopped");
    }
  }
}

function showSnackbar(text, color = "green") {
  if (actionEmitter) {
    actionEmitter.emit("snippet-show-snackbar", { text, color });
  }
}
</script>

<template>
  <div v-if="snippetActive" class="snippet-active-wrapper">
    <div class="snippet-active-bar">
      <v-icon class="snippet-pulse-icon" color="red" size="small">mdi-record</v-icon>
      <span class="snippet-elapsed">{{ elapsedLabel }}</span>
      <v-btn
        icon
        size="small"
        variant="text"
        :color="showDescription ? 'amber' : 'white'"
        @click="toggleDescription"
      >
        <v-icon>mdi-note-edit-outline</v-icon>
      </v-btn>
      <v-btn
        icon
        size="small"
        color="red"
        :loading="isStopping"
        @click="stopSnippet"
      >
        <v-icon>mdi-stop</v-icon>
      </v-btn>
    </div>
    <transition name="snippet-desc">
      <div v-if="showDescription" class="snippet-desc-panel">
        <v-combobox
          v-model="description"
          :items="cannedResponses"
          :label="$t('SnippetDescription')"
          density="compact"
          variant="outlined"
          hide-details
          class="snippet-combobox"
          clearable
          autofocus
          @keydown.enter="showDescription = false"
        />
      </div>
    </transition>
  </div>
</template>

<style scoped>
.snippet-active-wrapper {
  position: fixed;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.snippet-active-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: rgba(0, 0, 0, 0.85);
  border-radius: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.snippet-desc-panel {
  margin-top: 6px;
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.85);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  min-width: 260px;
}

.snippet-combobox {
  max-width: 260px;
  min-width: 220px;
}

.snippet-elapsed {
  font-size: 13px;
  color: #fff;
  font-variant-numeric: tabular-nums;
  min-width: 34px;
  text-align: center;
}

.snippet-pulse-icon {
  animation: snippet-pulse 1.2s ease-in-out infinite;
}

@keyframes snippet-pulse {
  0% { opacity: 1; }
  50% { opacity: 0.3; }
  100% { opacity: 1; }
}

.snippet-desc-enter-active,
.snippet-desc-leave-active {
  transition: all 0.2s ease;
}
.snippet-desc-enter-from,
.snippet-desc-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
